import React, { useEffect, useMemo, useState } from 'react';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { formatNumber } from '../../utils/formatters';
import {
  BustaGo, ETICHETTE_ORIGINE, FattureVoceCommessa, GruppoRiepilogo, GruppoTotali, MovimentoCommessa, PeriodoGo,
  RigaGo, RispostaDocumentiCommessa, RispostaMovimenti, RispostaRiepilogoCommessa, RispostaRighe,
  applicaPeriodo, dataIt, euro, num,
} from '../../lib/gestionaleOpen';
import { etichettaOrigineCommessa, nomeDocumento } from './ModaleDocumentiGo';

/**
 * Dettaglio di una voce di Analisi commesse (una commessa o sottocommessa).
 *
 * Quattro schede:
 * - Riepilogo: ricavi, costi e margine della voce per origine del movimento
 *   (fatture, DDT, prima nota, magazzino) e per conto. E' il bilancio della
 *   voce: tutti i movimenti di commessa di GO, non solo le fatture.
 * - Movimenti: i movimenti uno per uno, filtrabili dal riepilogo.
 * - Fatture di vendita / di acquisto: le fatture che valorizzano la voce, una
 *   riga per fattura, e al clic le sue righe. Sono solo una parte del
 *   bilancio: mancano personale, ratei, magazzino e le altre scritture.
 *
 * La riga «(senza commessa)» della tabella apre solo le schede fatture: sono
 * le parti di fattura non ancora collegate, che non sono movimenti di commessa.
 *
 * Chiama le route /commesse/* (permesso di Analisi commesse).
 */

/** Una commessa o sottocommessa della tabella. `codice` null = la riga «senza». */
export interface Voce {
  codice: string | null;
  etichetta: string | null;
}

export interface VoceAperta {
  commessa: Voce;
  /** Assente = tutta la commessa; con codice null = la parte senza sottocommessa. */
  sottocommessa?: Voce;
}

type Scheda = 'riepilogo' | 'movimenti' | 'ricavi' | 'costi';
type Lato = 'ricavi' | 'costi';

const LATI: Record<Lato, {
  vista: 'ricavi' | 'costi-fornitore'; scheda: string; fatture: string; nessuna: string; controparte: string;
}> = {
  ricavi: {
    vista: 'ricavi', scheda: 'Fatture di vendita', fatture: 'fatture di vendita', nessuna: 'Nessuna fattura di vendita',
    controparte: 'Cliente',
  },
  costi: {
    vista: 'costi-fornitore', scheda: 'Fatture di acquisto', fatture: 'fatture di acquisto', nessuna: 'Nessuna fattura di acquisto',
    controparte: 'Fornitore',
  },
};

const PAGINA_MOVIMENTI = 500;

/**
 * Filtri della voce per le route /commesse/*. `periodo` puo' essere solo
 * l'anno ('' = tutti). Esportata per i test.
 */
export function parametriVoce(societaId: string, periodo: PeriodoGo | string, voce: VoceAperta): URLSearchParams {
  const p = new URLSearchParams({ societaId });
  if (typeof periodo === 'string') {
    if (periodo) p.set('anno', periodo);
  } else {
    applicaPeriodo(p, periodo);
  }
  if (voce.commessa.codice) p.set('commessa', voce.commessa.codice);
  else p.set('senza_commessa', 'true');
  // Una sottocommessa ha senso solo dentro una commessa
  if (voce.commessa.codice && voce.sottocommessa) {
    if (voce.sottocommessa.codice) p.set('sottocommessa', voce.sottocommessa.codice);
    else p.set('senza_sottocommessa', 'true');
  }
  return p;
}

function nomeVoce(v: Voce, senza: string): string {
  if (!v.codice) return senza;
  return v.etichetta && v.etichetta !== v.codice ? `${v.etichetta} (${v.codice})` : v.codice;
}

function Margine({ valore }: { valore: number }) {
  return <span className={valore < 0 ? 'bop-text-danger' : undefined}>{euro(valore)}</span>;
}

/** Filtro attivo sulla scheda Movimenti. */
interface FiltroMovimenti {
  origine?: string;
  conto?: string;
  etichetta: string;
}

interface Props {
  societaId: string;
  periodo: PeriodoGo;
  voce: VoceAperta;
  onClose: () => void;
}

export default function ModaleMovimentiCommessa({ societaId, periodo, voce, onClose }: Props) {
  const senzaCommessa = !voce.commessa.codice;
  const [scheda, setScheda] = useState<Scheda>(senzaCommessa ? 'ricavi' : 'riepilogo');

  const chiaveFiltri = parametriVoce(societaId, periodo, voce).toString();

  // Riepilogo per origine e per conto
  const [origini, setOrigini] = useState<GruppoRiepilogo[] | null>(null);
  const [conti, setConti] = useState<GruppoRiepilogo[] | null>(null);
  const [erroreRiepilogo, setErroreRiepilogo] = useState('');

  // Movimenti
  const [filtroMov, setFiltroMov] = useState<FiltroMovimenti | null>(null);
  const [movimenti, setMovimenti] = useState<RispostaMovimenti | null>(null);
  const [caricandoMov, setCaricandoMov] = useState(false);
  const [erroreMov, setErroreMov] = useState('');

  // Fatture (caricate alla prima apertura di una delle due schede)
  const [fatture, setFatture] = useState<RispostaDocumentiCommessa | null>(null);
  const [caricandoFatture, setCaricandoFatture] = useState(false);
  const [erroreFatture, setErroreFatture] = useState('');
  const [documento, setDocumento] = useState<GruppoTotali | null>(null);
  const [righe, setRighe] = useState<RigaGo[] | null>(null);
  const [caricandoRighe, setCaricandoRighe] = useState(false);
  const [erroreRighe, setErroreRighe] = useState('');

  const lato: Lato | null = scheda === 'ricavi' || scheda === 'costi' ? scheda : null;

  // Esc chiude il modale
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Riepilogo: subito, e' il contenuto della prima scheda e dei totali in testa
  useEffect(() => {
    if (senzaCommessa) return undefined;
    let annullato = false;
    (async () => {
      setErroreRiepilogo('');
      try {
        const per = (raggruppa: string) => {
          const p = new URLSearchParams(chiaveFiltri);
          p.set('raggruppa', raggruppa);
          // @api GET /api/bop-go/dati-commerciali/commesse/riepilogo?societaId=1&anno=2025&commessa=PERSONALE2025&raggruppa=conto -> {"success":true,"stato":"OK","data":{"raggruppa":"conto","data":[{"codice":"CO010614","etichetta":"Salari e stipendi","ricavi":0,"costi":815009.18,"margine":-815009.18,"movimenti":13}]}}
          return apiFetch<BustaGo<RispostaRiepilogoCommessa>>(`/api/bop-go/dati-commerciali/commesse/riepilogo?${p.toString()}`);
        };
        const [o, c] = await Promise.all([per('origine'), per('conto')]);
        if (annullato) return;
        if (o.stato !== 'OK' || !o.data || !c.data) {
          setErroreRiepilogo(o.error || 'Dati GO non disponibili');
          return;
        }
        setOrigini(o.data.data);
        setConti(c.data.data);
      } catch (err) {
        if (annullato) return;
        setErroreRiepilogo(err instanceof Error ? err.message : 'Errore nel recupero del riepilogo da GO');
      }
    })();
    return () => { annullato = true; };
  }, [chiaveFiltri, senzaCommessa]);

  // Movimenti: alla scheda, con il filtro scelto; "carica altri" accoda
  const caricaMovimenti = async (offset: number) => {
    setCaricandoMov(true);
    setErroreMov('');
    try {
      const p = new URLSearchParams(chiaveFiltri);
      if (filtroMov?.origine) p.set('origine', filtroMov.origine);
      if (filtroMov?.conto) p.set('conto', filtroMov.conto);
      p.set('limit', String(PAGINA_MOVIMENTI));
      p.set('offset', String(offset));
      // @api GET /api/bop-go/dati-commerciali/commesse/movimenti?societaId=1&anno=2026&commessa=SMARTILIO2026&origine=contabilita&limit=500 -> {"success":true,"stato":"OK","data":{"count":"number","totale":"number","has_more":"boolean","data":[{"id_movimento":"number","costo_ricavo":"string","importo":"string","categoria_origine":"string","numero_documento":"string","controparte_nome":"string","conto_descrizione":"string"}]}}
      const res = await apiFetch<BustaGo<RispostaMovimenti>>(`/api/bop-go/dati-commerciali/commesse/movimenti?${p.toString()}`);
      if (res.stato !== 'OK' || !res.data) {
        setErroreMov(res.error || 'Dati GO non disponibili');
        return;
      }
      const nuovi = res.data;
      setMovimenti((prec) => (offset > 0 && prec ? { ...nuovi, data: [...prec.data, ...nuovi.data] } : nuovi));
    } catch (err) {
      setErroreMov(err instanceof Error ? err.message : 'Errore nel recupero dei movimenti da GO');
    } finally {
      setCaricandoMov(false);
    }
  };

  useEffect(() => {
    if (scheda !== 'movimenti') return;
    setMovimenti(null);
    void caricaMovimenti(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheda, filtroMov, chiaveFiltri]);

  // Fatture dei due lati: alla prima apertura di una scheda fatture
  useEffect(() => {
    if (!lato || fatture) return undefined;
    let annullato = false;
    (async () => {
      setCaricandoFatture(true);
      setErroreFatture('');
      try {
        // @api GET /api/bop-go/dati-commerciali/commesse/documenti?societaId=1&anno=2026&commessa=ESSELUNGA2026 -> {"success":true,"stato":"OK","data":{"database":"arc_solu","ricavi":{"documenti":50,"importo":1000000,"data":[{"codice":14224,"etichetta":"ESSELUNGA SPA","codice_documento":"FAIV","descrizione_documento":"FATTURA IMMEDIATA","numero_documento":"292","righe":1,"importo":"81541.68","prima_data":"2026-07-31"}]},"costi":{"documenti":20,"importo":790220.46,"data":[]}}}
        const res = await apiFetch<BustaGo<RispostaDocumentiCommessa>>(`/api/bop-go/dati-commerciali/commesse/documenti?${chiaveFiltri}`);
        if (annullato) return;
        if (res.stato !== 'OK' || !res.data) {
          setErroreFatture(res.error || 'Dati GO non disponibili');
          return;
        }
        setFatture(res.data);
        // Sulla riga «senza commessa» si apre sul lato che ha qualcosa da mostrare
        if (senzaCommessa && res.data.ricavi.data.length === 0 && res.data.costi.data.length > 0) setScheda('costi');
      } catch (err) {
        if (annullato) return;
        setErroreFatture(err instanceof Error ? err.message : 'Errore nel recupero delle fatture da GO');
      } finally {
        if (!annullato) setCaricandoFatture(false);
      }
    })();
    return () => { annullato = true; };
  }, [lato, fatture, chiaveFiltri, senzaCommessa]);

  useEffect(() => {
    if (!lato || !documento || documento.codice === null || documento.codice === undefined) { setRighe(null); return undefined; }
    let annullato = false;
    (async () => {
      setCaricandoRighe(true);
      setErroreRighe('');
      setRighe(null);
      try {
        const p = new URLSearchParams(chiaveFiltri);
        p.set('vista', LATI[lato].vista);
        p.set('documento', String(documento.codice));
        // @api GET /api/bop-go/dati-commerciali/commesse/righe?societaId=1&anno=2026&commessa=ESSELUNGA2026&vista=costi-fornitore&documento=3446 -> {"success":true,"stato":"OK","data":{"count":"number","totale":"number","has_more":"boolean","data":[]}}
        const res = await apiFetch<BustaGo<RispostaRighe>>(`/api/bop-go/dati-commerciali/commesse/righe?${p.toString()}`);
        if (annullato) return;
        setRighe(res.data?.data ?? []);
        if (res.stato !== 'OK' && res.error) setErroreRighe(res.error);
      } catch (err) {
        if (annullato) return;
        setErroreRighe(err instanceof Error ? err.message : 'Errore nel recupero delle righe da GO');
      } finally {
        if (!annullato) setCaricandoRighe(false);
      }
    })();
    return () => { annullato = true; };
  }, [documento, lato, chiaveFiltri]);

  // ── Colonne ───────────────────────────────────────────────────────────────
  const colonneRiepilogo = (intestazione: string, nome: (g: GruppoRiepilogo) => string): BopExcelColumn<GruppoRiepilogo>[] => [
    { key: 'nome', header: intestazione, accessor: nome, filter: { type: 'text' } },
    { key: 'ricavi', header: 'Ricavi', accessor: (g) => g.ricavi, render: (g) => euro(g.ricavi), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '130px' },
    { key: 'costi', header: 'Costi', accessor: (g) => g.costi, render: (g) => euro(g.costi), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '130px' },
    { key: 'margine', header: 'Margine', accessor: (g) => g.margine, render: (g) => <Margine valore={g.margine} />, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '130px' },
    { key: 'movimenti', header: 'Movimenti', accessor: (g) => g.movimenti, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '100px' },
  ];
  const colonneOrigini = useMemo(
    () => colonneRiepilogo('Origine', (g) => ETICHETTE_ORIGINE[g.codice ?? ''] ?? g.codice ?? '—'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const colonneConti = useMemo(
    () => colonneRiepilogo('Conto', (g) => (g.codice ? `${g.etichetta ?? ''} (${g.codice})`.trim() : '(conto non indicato)')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const colonneMovimenti = useMemo<BopExcelColumn<MovimentoCommessa>[]>(() => [
    { key: 'data', header: 'Data', accessor: (m) => m.data_riferimento ?? '', render: (m) => dataIt(m.data_riferimento), width: '110px' },
    { key: 'origine', header: 'Origine', accessor: (m) => ETICHETTE_ORIGINE[m.categoria_origine] ?? m.categoria_origine, filter: { type: 'text' }, width: '190px' },
    {
      key: 'documento',
      header: 'Documento',
      // Le scritture di prima nota senza documento non hanno un numero da mostrare
      accessor: (m) => (m.documento_tipo && m.numero_documento ? `${m.documento_tipo} n. ${m.numero_documento}` : ''),
      width: '170px',
    },
    { key: 'controparte', header: 'Controparte', accessor: (m) => m.controparte_nome ?? '', filter: { type: 'text' } },
    {
      key: 'conto',
      header: 'Conto',
      accessor: (m) => m.conto_descrizione ?? m.conto_codice ?? '',
      filter: { type: 'text' },
    },
    { key: 'descrizione', header: 'Descrizione', accessor: (m) => m.descrizione ?? '', filter: { type: 'text' }, defaultHidden: true },
    {
      key: 'tipo',
      header: 'Tipo',
      accessor: (m) => m.costo_ricavo,
      render: (m) => (m.costo_ricavo === 'ricavo'
        ? <span className="bop-badge bop-badge-success">ricavo</span>
        : <span className="bop-badge bop-badge-neutral">costo</span>),
      width: '90px',
    },
    {
      key: 'importo',
      header: 'Importo',
      accessor: (m) => num(m.importo),
      render: (m) => euro(m.importo),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '130px',
    },
  ], []);

  const quota = senzaCommessa ? 'Da attribuire' : 'Quota commessa';
  const conSottocommessa = !senzaCommessa && !voce.sottocommessa;

  const colonneFatture = useMemo<BopExcelColumn<GruppoTotali>[]>(() => [
    { key: 'data', header: 'Data', accessor: (d) => d.prima_data ?? '', render: (d) => dataIt(d.prima_data), width: '120px' },
    { key: 'documento', header: 'Documento', accessor: (d) => nomeDocumento(d), filter: { type: 'text' }, width: '230px' },
    { key: 'controparte', header: lato ? LATI[lato].controparte : '', accessor: (d) => d.etichetta ?? '', filter: { type: 'text' } },
    { key: 'righe', header: 'Righe', accessor: (d) => d.righe, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '80px' },
    {
      key: 'importo', header: quota, accessor: (d) => num(d.importo), render: (d) => euro(d.importo),
      cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', filter: { type: 'number' }, width: '150px',
    },
  ], [lato, quota]);

  const colonneRighe = useMemo<BopExcelColumn<RigaGo>[]>(() => {
    const cols: BopExcelColumn<RigaGo>[] = [
      { key: 'riga', header: 'Riga', accessor: (r) => r.riga, width: '70px' },
      { key: 'articolo', header: 'Articolo', accessor: (r) => r.codice_articolo ?? '', width: '140px' },
      { key: 'descrizione', header: 'Descrizione', accessor: (r) => r.DESCRIZIONE1 ?? '', filter: { type: 'text' } },
    ];
    if (conSottocommessa) {
      cols.push({
        key: 'sottocommessa',
        header: 'Sottocommessa',
        accessor: (r) => r.codice_sottocommessa ?? '',
        render: (r) => (r.codice_sottocommessa
          ? <span className="bop-badge bop-badge-info">{r.codice_sottocommessa}</span>
          : <span className="bop-text-muted">—</span>),
        width: '150px',
      });
    }
    cols.push({ key: 'origine', header: 'Da dove', accessor: (r) => etichettaOrigineCommessa(r.origine_commessa), width: '140px' });
    cols.push({
      key: 'importo',
      header: quota,
      // importo_con_segno = IMPORTO × segno: le note di credito si sottraggono
      accessor: (r) => num(r.importo_con_segno ?? r.IMPORTO),
      render: (r) => euro(r.importo_con_segno ?? r.IMPORTO),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '140px',
    });
    return cols;
  }, [conSottocommessa, quota]);

  const totaleRighe = useMemo(
    () => (righe ?? []).reduce((s, r) => s + num(r.importo_con_segno ?? r.IMPORTO), 0),
    [righe]
  );

  // ── Totali in testa: tutti i movimenti della voce ─────────────────────────
  const totale = useMemo(() => {
    if (!origini) return null;
    return origini.reduce(
      (t, g) => ({ ricavi: t.ricavi + g.ricavi, costi: t.costi + g.costi, margine: t.margine + g.margine, movimenti: t.movimenti + g.movimenti }),
      { ricavi: 0, costi: 0, margine: 0, movimenti: 0 },
    );
  }, [origini]);
  const marginePct = totale && totale.ricavi
    ? `${formatNumber(Math.round((totale.margine / totale.ricavi) * 1000) / 10)}%`
    : null;

  const corrente: FattureVoceCommessa | null = fatture && lato ? fatture[lato] : null;

  const titolo = senzaCommessa ? 'Fatture senza commessa' : nomeVoce(voce.commessa, '');
  const sottotitolo = lato && documento
    ? `${nomeDocumento(documento)} — ${documento.etichetta ?? ''}`
    : senzaCommessa
      ? 'Le parti di fattura non ancora collegate a una commessa'
      : voce.sottocommessa
        ? `Sottocommessa ${nomeVoce(voce.sottocommessa, '(senza sottocommessa)')}`
        : 'Tutte le sottocommesse';

  const vaiAScheda = (s: Scheda) => { setScheda(s); setDocumento(null); };
  const apriMovimenti = (f: FiltroMovimenti | null) => { setFiltroMov(f); setScheda('movimenti'); };

  const schede: Array<{ id: Scheda; etichetta: string }> = senzaCommessa
    ? [
      { id: 'ricavi', etichetta: LATI.ricavi.scheda },
      { id: 'costi', etichetta: LATI.costi.scheda },
    ]
    : [
      { id: 'riepilogo', etichetta: 'Riepilogo' },
      { id: 'movimenti', etichetta: 'Movimenti' },
      { id: 'ricavi', etichetta: LATI.ricavi.scheda },
      { id: 'costi', etichetta: LATI.costi.scheda },
    ];

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div
        className="bop-modal bop-modal--xl bop-dettaglio-go"
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bop-modal__header">
          <div className="bop-dettaglio-go__intestazione">
            <h2 className="bop-modal__title">{titolo}</h2>
            <div className="bop-dettaglio-go__sottotitolo">{sottotitolo}</div>
          </div>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>

        <div className="bop-modal__body">
          {!senzaCommessa && totale && (
            <div className="bop-dettaglio-go__riepilogo">
              <span className="bop-dettaglio-go__riepilogo-voce">Ricavi <strong>{euro(totale.ricavi)}</strong></span>
              <span className="bop-dettaglio-go__riepilogo-voce">Costi <strong>{euro(totale.costi)}</strong></span>
              <span className="bop-dettaglio-go__riepilogo-voce">
                Margine <strong><Margine valore={totale.margine} /></strong>
                {marginePct && <> · {marginePct} dei ricavi</>}
              </span>
              <span className="bop-dettaglio-go__riepilogo-voce">{totale.movimenti} movimenti</span>
            </div>
          )}

          <div className="bop-tabs">
            {schede.map((s) => (
              <button key={s.id} className={`bop-tab ${scheda === s.id ? 'active' : ''}`} onClick={() => vaiAScheda(s.id)}>
                {s.etichetta}
              </button>
            ))}
          </div>

          {/* ── Riepilogo ─────────────────────────────────────────────────── */}
          {scheda === 'riepilogo' && (
            <>
              {erroreRiepilogo && <div className="bop-alert bop-alert--danger">{erroreRiepilogo}</div>}
              {!origini && !erroreRiepilogo && <div className="bop-empty-state">Lettura dei movimenti…</div>}
              {origini && conti && (
                <>
                  <p className="bop-text-muted bop-dettaglio-go__nota">
                    Tutti i movimenti di commessa di GO: fatture, DDT, prima nota (personale, ratei,
                    rettifiche) e scarichi di magazzino. Clic su una riga per vederne i movimenti.
                  </p>
                  <h3 className="bop-dettaglio-go__titolo-sezione">Per origine</h3>
                  <BopExcelTable<GruppoRiepilogo>
                    data={origini}
                    columns={colonneOrigini}
                    rowKey={(g) => g.codice ?? 'nessuna'}
                    onRowClick={(g) => apriMovimenti(g.codice
                      ? { origine: g.codice, etichetta: ETICHETTE_ORIGINE[g.codice] ?? g.codice }
                      : null)}
                    initialSort={{ key: 'costi', dir: 'desc' }}
                    storageKey="commesse-go-riepilogo-origini"
                    emptyState={<div className="bop-empty-state">Nessun movimento nel periodo</div>}
                  />
                  <h3 className="bop-dettaglio-go__titolo-sezione">Per conto</h3>
                  <BopExcelTable<GruppoRiepilogo>
                    data={conti}
                    columns={colonneConti}
                    rowKey={(g) => g.codice ?? 'nessuno'}
                    onRowClick={(g) => apriMovimenti(g.codice
                      ? { conto: g.codice, etichetta: g.etichetta ?? g.codice }
                      : null)}
                    initialSort={{ key: 'costi', dir: 'desc' }}
                    storageKey="commesse-go-riepilogo-conti"
                    emptyState={<div className="bop-empty-state">Nessun movimento nel periodo</div>}
                  />
                  <p className="bop-text-muted bop-dettaglio-go__nota">
                    Il conto c'è sui movimenti di prima nota; per fatture, DDT e magazzino resta
                    «conto non indicato».
                  </p>
                </>
              )}
            </>
          )}

          {/* ── Movimenti ─────────────────────────────────────────────────── */}
          {scheda === 'movimenti' && (
            <>
              {filtroMov && (
                <div className="bop-dettaglio-go__filtro-attivo">
                  <span className="bop-badge bop-badge-info">{filtroMov.etichetta}</span>
                  <button className="bop-btn bop-btn-secondary" onClick={() => setFiltroMov(null)}>Tutti i movimenti</button>
                </div>
              )}
              {erroreMov && <div className="bop-alert bop-alert--danger">{erroreMov}</div>}
              {caricandoMov && !movimenti && <div className="bop-empty-state">Lettura dei movimenti…</div>}
              {movimenti && (
                <>
                  <BopExcelTable<MovimentoCommessa>
                    data={movimenti.data}
                    columns={colonneMovimenti}
                    rowKey={(m) => (m.id_movimento !== null ? String(m.id_movimento) : `fattura-${m.documento_progressivo}-${m.documento_riga}-${m.codice_sottocommessa ?? ''}`)}
                    loading={caricandoMov}
                    initialSort={{ key: 'data', dir: 'desc' }}
                    storageKey="commesse-go-movimenti"
                    emptyState={<div className="bop-empty-state">Nessun movimento</div>}
                  />
                  <div className="bop-dettaglio-go__totale-righe">
                    {movimenti.data.length} movimenti su {movimenti.totale}
                  </div>
                  {movimenti.has_more && (
                    <button
                      className="bop-btn bop-btn-secondary"
                      disabled={caricandoMov}
                      onClick={() => void caricaMovimenti(movimenti.data.length)}
                    >
                      Carica altri {PAGINA_MOVIMENTI}
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Fatture ───────────────────────────────────────────────────── */}
          {lato && (
            <>
              {erroreFatture && <div className="bop-alert bop-alert--danger">{erroreFatture}</div>}
              {caricandoFatture && <div className="bop-empty-state">Lettura delle fatture…</div>}

              {!caricandoFatture && corrente && !documento && (
                <>
                  <p className="bop-text-muted bop-dettaglio-go__nota">
                    {senzaCommessa
                      ? 'Per ogni fattura, la parte non ancora collegata a una commessa.'
                      : `${corrente.documenti} ${LATI[lato].fatture} per ${euro(corrente.importo)}: per ognuna la sola parte attribuita a questa commessa. Sono una parte del bilancio della commessa, che comprende anche prima nota e magazzino.`}
                    {' '}Clic su una fattura per vederne le righe.
                  </p>
                  <BopExcelTable<GruppoTotali>
                    data={corrente.data}
                    columns={colonneFatture}
                    rowKey={(d) => String(d.codice)}
                    onRowClick={(d) => setDocumento(d)}
                    initialSort={{ key: 'data', dir: 'desc' }}
                    storageKey={`commesse-go-fatture-${lato}`}
                    emptyState={<div className="bop-empty-state">{LATI[lato].nessuna} nel periodo</div>}
                  />
                </>
              )}

              {documento && (
                <>
                  <button className="bop-btn bop-btn-secondary bop-dettaglio-go__indietro" onClick={() => setDocumento(null)}>
                    ← Tutte le {LATI[lato].fatture}
                  </button>
                  <div className="bop-dettaglio-go__riepilogo">
                    <span className="bop-dettaglio-go__riepilogo-voce">
                      Data <strong>{dataIt(documento.prima_data)}</strong>
                    </span>
                    <span className="bop-dettaglio-go__riepilogo-voce">
                      {LATI[lato].controparte} <strong>{documento.etichetta ?? '—'}</strong>
                    </span>
                    <span className="bop-dettaglio-go__riepilogo-voce">
                      {quota} <strong>{euro(documento.importo)}</strong>
                    </span>
                  </div>
                  {erroreRighe && <div className="bop-alert bop-alert--danger">{erroreRighe}</div>}
                  {caricandoRighe && !righe && <div className="bop-empty-state">Lettura delle righe…</div>}
                  {righe && (
                    <>
                      <BopExcelTable<RigaGo>
                        data={righe}
                        columns={colonneRighe}
                        rowKey={(r) => `${r.progressivo}-${r.riga}-${r.id_movimento_commessa ?? ''}-${r.origine_commessa ?? ''}`}
                        loading={caricandoRighe}
                        storageKey="commesse-go-righe"
                        emptyState={<div className="bop-empty-state">Nessuna riga</div>}
                      />
                      <div className="bop-dettaglio-go__totale-righe">
                        {righe.length} righe · totale <strong>{euro(totaleRighe)}</strong>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
